import React, { Component } from 'react';
import { Map } from 'immutable';
import { PropTypes } from 'prop-types';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { passwordUtilities } from './../../common';
import * as changePasswordActions from './change-password.actions';
import { toastr } from 'react-redux-toastr';
import { formDefinition } from '@social-solutions/ssg-forms-processing';
import { SsgFormComponent, ssgFormActions, Widget } from '@social-solutions/ssg-forms';
import formStyle from '../account-widget.style';
import './change-password.scss';

const formComponentId = "change-password-form-response";
const currentPasswordId = 'change-password-current-password';
const newPasswordId = 'change-password-new-password';
const passwordConfirmationId = 'change-password-confirm-password';

const currentPasswordElement = {
  id: currentPasswordId,
  type: formDefinition.elementTypes.specialText,
  label: "Current Password",
  ariaLabel: "Current Password",
  isPassword: true,
  textType: formDefinition.specialTextTypes.ANY,
  isRequired: true,
};

const newPasswordElement = {
  id: newPasswordId,
  type: formDefinition.elementTypes.specialText,
  label: "New Password",
  ariaLabel: "New Password",
  isPassword: true,
  textType: formDefinition.specialTextTypes.ANY,
  isRequired: true,
};

const confirmPasswordElement = {
  id: passwordConfirmationId,
  type: formDefinition.elementTypes.specialText,
  label: "Confirm New Password",
  ariaLabel: "Confirm New Password",
  isPassword: true,
  textType: formDefinition.specialTextTypes.ANY,
  isRequired: true,
};

export class ChangePasswordComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      customValidationErrors: [],
      updating: false,
    }
  }

  /**
   * Handles the save button being pressed
   * @param {*} state the current form state when save is clicked
   */
  handleFormSubmit(state) {
    const { changePasswordActions, ssgFormActions } = this.props;
    const { customValidationErrors } = this.state;
    const { createNewPassword } = changePasswordActions;
    const { resetForm, processForm } = ssgFormActions;

    if(customValidationErrors.length > 0 || !state.validationResult.passedValidation) {
      const firstError = state.validationResult.errors.first();
      const errorElement = firstError && document.getElementById(firstError.sourceId);
      errorElement && errorElement.focus();
      return;
    }

    const currentPassword = state.value[currentPasswordId];
    const newPassword = state.value[newPasswordId];

    createNewPassword(currentPassword, newPassword)
    .then( () => {
      toastr.success('Success', 'Password Changed');
      resetForm(formComponentId);
      processForm(formComponentId);
      this.setState({updating: false});
    })
    .catch( err => {
      toastr.error(err.errorCode, err.errorMessage);
      throw(err);
    });
  }

  /**
   * Handles the cancel button being clicked.
   * Changes back to read only mode and clears any changes
   * @param {*} e the current form state when cancel is clicked
   */
  handleCancel(e) {
    this.setState({updating: false});
  }

  /**
   * Handles the update button being clicked, enters edit mode
   */
  handleUpdate() {
    this.setState({updating: true});
  }

  /**
   * Runs validation that isn't standard for the form
   * @param {object} formState
   */
  runCustomValidation(formState) {
    const { settings } = this.props;

    const pwRules = !!settings && !!settings.passwordRules ? settings.passwordRules : null;
    var validationErrors = passwordUtilities.validatePasswordChange(formState, newPasswordId, passwordConfirmationId, pwRules);

    this.setState({customValidationErrors: validationErrors});
  }

  /** renders the form for edit mode */
  renderEditForm() {
    const { changePassword } = this.props;
    const { customValidationErrors } = this.state;
    const { isFetching } = changePassword;

    var saveButton = {
      id: "save-button",
      type: "submit",
      display: "Save",
      ariaLabel: "Save",
      buttonType: "primary",
      functionType: "submit",
      spinnerOn: isFetching,
      spinnerDisplay: "Saving",
      onClick: (state) => {
        this.handleFormSubmit(state);
      },
    };

    var cancelButton = {
      id: "cancel-button",
      type: "button",
      display: "Cancel",
      ariaLabel: "Cancel",
      buttonType: "default",
      functionType: "reset",
      spinnerOn: false,
      disabled: isFetching,
      onClick: (state) => this.handleCancel(state),
    }

    const formProps = {
      id: formComponentId,
      form: {
        id: formComponentId,
        heading: "Account Password",
        elements: [ currentPasswordElement, newPasswordElement, confirmPasswordElement ]
      },
      disableValidationResultList: true,
      elementStyles: formStyle,
      buttonLayout: [ cancelButton, saveButton ],
      customValidationErrors,
      css: {
        title: "ssg-forms-text large account-widget-title",
        actions: "ssg-forms-plain-actions",
      }
    };

    return <SsgFormComponent {...formProps} />;
  }

  /** renders the form for readonly mode */
  renderReadOnlyForm() {

    var updateButton = {
      id: "password-update-button",
      type: "button",
      display: "Update",
      ariaLabel: "Update",
      buttonType: "secondary",
      functionType: "none",
      onClick: () => this.handleUpdate(),
    };

    const formProps = {
      id: formComponentId,
      readOnly: true,
      form: {
        id: formComponentId,
        heading: "Account Password",
        elements: [{
          ...currentPasswordElement,
          label: "Password",
          ariaLabel: "Password",
          isRequired: false,
        }]
      },
      disableValidationResultList: true,
      elementStyles: formStyle,
      buttonLayout: [ updateButton ],
      initialValues:  Map({
        [currentPasswordElement.id]: "*************",
      }),
      css: {
        title: "ssg-forms-text large account-widget-title",
        actions: "ssg-forms-plain-actions",
      }
    };

    return <SsgFormComponent {...formProps} />;
  }

  render() {
    const { updating } = this.state;

    return(
      <Widget css={{widgetDisplay: 'ssg-forms-widget account-widget'}}>
        {updating ? this.renderEditForm() : this.renderReadOnlyForm()}
      </Widget>
    );
  }
}

ChangePasswordComponent.propTypes = {
  auth: PropTypes.object.isRequired,
  login: PropTypes.any.isRequired,
  changePassword: PropTypes.any.isRequired,
  changePasswordActions: PropTypes.any.isRequired,
  settings: PropTypes.any
};

const mapStateToProps = ({ auth, login, changePassword, settings, ssgForms }) => {
  return {
    auth,
    login,
    changePassword,
    settings,
    ssgForms
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    changePasswordActions: bindActionCreators(changePasswordActions, dispatch),
    ssgFormActions: bindActionCreators(ssgFormActions, dispatch),
    redirectTo: bindActionCreators(push, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChangePasswordComponent);



// WEBPACK FOOTER //
// src/acct/password/change-password.component.js